import { createElement, FC, useState } from 'react'

import { RuleID } from '@forto-dev/insights-engine-models'
import { ChannelType, CorrelationType } from '@forto-dev/notification-center-models'
import {
  Button,
  ChevronDownIcon,
  ChevronUpIcon,
  Cluster,
  Separator,
  Stack,
  Typography,
} from '@forto-dev/phoenix-ui-core'

import { getActivityRenderedContent } from '@/features/feed/view/getActivityRenderedContent'
import { ActivityFeedItemProps } from '@/features/feed/view/types.ts'
import { MOTUsefulnessSurvey, useIsSurveyEnabled } from '@/features/notifications'
import { useLocalisation } from '@/utils/localisation'
import { ShadowDOM } from '@/utils/shadowDOM.tsx'

const defaultExpandedRuleIDs: RuleID[] = [RuleID.MoT02_EV02_BookingConfirmedByCarrier]
type InsightBlockProps = { html: string | undefined }
export const InsightBlock: FC<InsightBlockProps> = ({ html }) => {
  if (!html) {
    return null
  }

  // transform html
  const replacements: [RegExp, string][] = [
    [/Roboto/g, 'Inter'], // To match font
    [/margin:0 auto;?/g, ''], // To remove centering
    [/max-width:422px;?/g, ''], // To expand scrollable tables to max width
    [/max-width:600px;?/g, ''], // To expand dividers to max width
    [/padding-top:16px;?/, ''], // To remove first padding top. Not using /g flag to avoid removing all padding
    [/color:rgba\(15,42,73,.65\)/g, ''], // To remove light grey text color of main content
    [/line-height:20px/, ''],
    [/padding:32px/, 'padding:32px 0'], // To remove the left padding of Email notification title displayed in NC
    [/padding:0 32px 0 72px/, ''], // To remove the left padding of Email notification content displayed in NC
    [/max-width:822px;?/g, ''], // To reset max width to 100% for email notification in NC
    [/max-width:718px;?/g, ''], // To reset max width to 100% for email notification in NC
    [/#1a74e8/gi, 'var(--typography-primary)'], // To make links and buttons match the primary color
  ] as const

  let updatedHTML = html
  replacements.forEach(([pattern, replacement]) => {
    updatedHTML = updatedHTML.replace(pattern, replacement)
  })
  return (
    <>
      {/* TODO: this Typography component should be a div, since ShadowDom is a div and divs cannot be children of <p>, which is Typography by default */}
      <Typography>
        <ShadowDOM>
          {createElement('span', {
            dangerouslySetInnerHTML: {
              __html: updatedHTML,
            },
          })}
        </ShadowDOM>
      </Typography>
    </>
  )
}
export const InsightActivityContent: FC<ActivityFeedItemProps> = ({ item }) => {
  const { currentLanguage, translate } = useLocalisation()
  const notificationReference = item.activity.customField?.ruleID as RuleID // TODO: improve this type
  const shouldBeExpandedAlways = defaultExpandedRuleIDs.includes(notificationReference)
  const [isExpanded, setIsExpanded] = useState(shouldBeExpandedAlways)
  const isSurveyEnabled = useIsSurveyEnabled(notificationReference)

  const renderedContent = item.activity.customField?.renderedContent
  if (!renderedContent) return null

  const { main, expanded: expandedContent } = getActivityRenderedContent(renderedContent, currentLanguage)

  const shouldShowSurvey = isSurveyEnabled && notificationReference
  return (
    <Stack gap={16}>
      {main && <InsightBlock html={main} />}
      {expandedContent ? (
        <>
          {isExpanded && (
            <>
              <InsightBlock html={expandedContent} />
              {shouldShowSurvey && (
                <>
                  <Separator orientation="horizontal" />
                  <MOTUsefulnessSurvey
                    reference={notificationReference}
                    channelType={ChannelType.ActivityFeed}
                    correlationID={item.id}
                    correlationType={CorrelationType.ActivityFeed}
                  />
                  <Separator orientation="horizontal" />
                </>
              )}
            </>
          )}

          {!shouldBeExpandedAlways && (
            <Button size="sm" variant="outline-secondary" onClick={() => setIsExpanded((expanded) => !expanded)}>
              <Cluster gap={8} y="center">
                <Typography variant="small">
                  {isExpanded ? translate('feed.expand.less') : translate('feed.expand.more')}
                </Typography>
                {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
              </Cluster>
            </Button>
          )}
        </>
      ) : null}
    </Stack>
  )
}
